import { useMutation, useQuery } from '@tanstack/react-query';
import type { Feature } from 'geojson';
import { useMemo, useState } from 'react';

import { api } from '../../api/endpoints';
import type { Area } from '../../api/types';
import { LazyMapView as MapView } from '../../components/LazyMapView';
import type { MapShapeLayer, MapViewport } from '../../components/MapView';
import { MutationError } from '../../components/MutationError';
import { areaLookup, cssVar, featureBounds, loadPlzIndex, prefixesIn, shapeAttribution, shapeCode, shapeName } from '../../map/shapes';
import { useAreaMap } from '../../map/useAreaMap';

type Kind = 'COUNTY' | 'POSTAL_CODE';

// PLZ erst ab dieser Zoomstufe und für höchstens so viele Leitbereiche gleichzeitig laden.
const plzMinZoom = 9;
const plzMaxPrefixes = 12;

// AreaMapEditor: Zusammensetzung eines Gebiets auf der Karte – Klick auf einen Landkreis bzw. eine PLZ fügt sie hinzu
// oder entfernt sie wieder. Flächen anderer Gebiete des Projekts sind grau hinterlegt.
export function AreaMapEditor({ area, editable, onSaved }: { area: Area; editable: boolean; onSaved: (a: Area) => void }) {
  const [kind, setKind] = useState<Kind>('COUNTY');
  const [last, setLast] = useState<string>();
  const [view, setView] = useState<MapViewport>();
  const index = useQuery({ queryKey: ['karten', 'plz-index'], queryFn: loadPlzIndex, enabled: kind === 'POSTAL_CODE', staleTime: Infinity });
  const own = area.elements.filter((e) => e.type === 'POSTAL_CODE').map((e) => e.code.slice(0, 2));
  const visible = kind === 'POSTAL_CODE' && view && view.zoom >= plzMinZoom && index.data ? prefixesIn(index.data, view) : [];
  const { shapes, counties, plz } = useAreaMap(true, kind === 'POSTAL_CODE' ? [...own, ...(visible.length <= plzMaxPrefixes ? visible : [])] : []);
  const zoomHint = kind === 'POSTAL_CODE' && (!view || view.zoom < plzMinZoom);
  const toggle = useMutation({
    mutationFn: async (f: Feature) => {
      const code = shapeCode(f);
      const existing = area.elements.find((e) => e.type === kind && e.code === code);
      const next = existing ? await api.removeAreaElement(area.id, existing.id) : await api.addAreaElement(area.id, { type: kind, code });
      setLast(`${kind === 'COUNTY' ? shapeName(f) : `PLZ ${code}`} ${existing ? 'entfernt' : 'hinzugefügt'}.`);
      return next;
    },
    onSuccess: onSaved,
  });

  const layers = useMemo<MapShapeLayer[]>(() => {
    const own = new Set(area.elements.filter((e) => e.type === kind).map((e) => e.code));
    const lookup = areaLookup(shapes.data);
    const primary = cssVar('--color-primary');
    const muted = cssVar('--color-muted');
    const border = cssVar('--color-border-strong');
    const features = (kind === 'COUNTY' ? counties.data : plz.data) ?? [];
    const other = (code: string) => {
      const a = kind === 'COUNTY' ? lookup.byCounty.get(code) : lookup.byPostalCode.get(code);
      return a && a.area_id !== area.id ? a : undefined;
    };
    return [
      {
        id: `edit-${kind}`,
        features,
        attribution: kind === 'COUNTY' ? shapeAttribution.kreise : shapeAttribution.plz,
        style: (f) => {
          const code = shapeCode(f);
          if (own.has(code)) return { color: primary, weight: 1.5, fillColor: primary, fillOpacity: 0.4 };
          if (other(code)) return { color: muted, weight: 0.6, fillColor: muted, fillOpacity: 0.18 };
          // Ohne Füllung, aber anklickbar (fill bleibt an).
          return { color: border, weight: kind === 'COUNTY' ? 0.5 : 0.6, fill: true, fillOpacity: 0 };
        },
        tooltip: (f) => {
          const code = shapeCode(f);
          const o = other(code);
          const label = kind === 'COUNTY' ? shapeName(f) : `PLZ ${code}`;
          if (own.has(code)) return `${label}: in diesem Gebiet${editable ? ' – klicken zum Entfernen' : ''}`;
          return `${label}${o ? ` (Gebiet ${o.name})` : ''}${editable ? ' – klicken zum Hinzufügen' : ''}`;
        },
        onClick: editable ? (f) => !toggle.isPending && toggle.mutate(f) : undefined,
      },
    ];
  }, [area.elements, area.id, counties.data, editable, kind, plz.data, shapes.data, toggle]);

  // Ausschnitt: das Gebiet selbst, sonst alle Gebiete des Projekts (ohne Bestandteile: Deutschland).
  const bounds = useMemo(() => {
    const own = new Set(area.elements.filter((e) => e.type === 'COUNTY' || e.type === 'POSTAL_CODE').map((e) => e.code));
    const all = [...(counties.data ?? []), ...(plz.data ?? [])];
    return featureBounds(all.filter((f) => own.has(shapeCode(f))));
  }, [area.elements, counties.data, plz.data]);
  const loading = kind === 'COUNTY' ? counties.isPending : plz.isPending;
  const failed = kind === 'COUNTY' ? counties.isError : plz.isError;
  return (
    <div className="area-map-editor">
      <div className="area-map-toolbar">
        <div className="chips" role="group" aria-label="Flächen auf der Karte">
          {(
            [
              ['COUNTY', 'Landkreise'],
              ['POSTAL_CODE', 'PLZ'],
            ] as [Kind, string][]
          ).map(([k, label]) => (
            <button key={k} type="button" className={kind === k ? 'chip active' : 'chip'} aria-pressed={kind === k} onClick={() => setKind(k)}>
              {label}
            </button>
          ))}
        </div>
        <span className="muted small" aria-live="polite">
          {toggle.isPending
            ? 'Wird gespeichert …'
            : zoomHint
              ? 'Für PLZ näher heranzoomen – dann erscheinen die PLZ-Gebiete des Ausschnitts.'
              : (last ?? (editable ? 'Klick auf eine Fläche fügt sie hinzu oder entfernt sie.' : 'Nur Ansicht.'))}
        </span>
      </div>
      {failed ? (
        <p className="alert alert-warn">Die Flächen konnten nicht geladen werden – bitte die Liste unten verwenden.</p>
      ) : (
        <MapView
          markers={[]}
          shapes={loading ? [] : layers}
          bounds={bounds}
          onViewChange={setView}
          ariaLabel={`Karte: Zusammensetzung von ${area.name}`}
          fit={false}
          className="map-medium"
        />
      )}
      {toggle.isError && <MutationError error={toggle.error} />}
    </div>
  );
}
